import { Space, Radio, DatePicker, Tag } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import type { RangePreset } from '@/features/ops/api/ops.api';

const { RangePicker } = DatePicker;

export type RangeState =
  | { preset: Exclude<RangePreset, 'CUSTOM'> }
  | { preset: 'CUSTOM'; from: string; to: string };

export function presetLabelKey(p: RangePreset): string {
  switch (p) {
    case 'TODAY': return 'dashboard.range.today';
    case 'YESTERDAY': return 'dashboard.range.yesterday';
    case 'LAST_3_DAYS': return 'dashboard.range.last3Days';
    case 'LAST_7_DAYS': return 'dashboard.range.last7Days';
    case 'LAST_30_DAYS': return 'dashboard.range.last30Days';
    case 'CUSTOM': return 'dashboard.range.custom';
  }
}

interface Props {
  value: RangeState;
  onChange: (next: RangeState) => void;
  appliedFrom?: string | null;
  appliedTo?: string | null;
}

export default function DashboardRangeFilter({ value, onChange, appliedFrom, appliedTo }: Props) {
  const { t } = useTranslation();

  const presets: RangePreset[] = ['TODAY', 'YESTERDAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM'];

  const rangeValue: [Dayjs, Dayjs] | undefined =
    value.preset === 'CUSTOM' && value.from && value.to
      ? [dayjs(value.from), dayjs(value.to)]
      : undefined;

  return (
    <Space wrap size={[12, 8]} align="center">
      <span style={{ color: '#8c8c8c', fontSize: 12 }}>{t('dashboard.range.label')}:</span>
      <Radio.Group
        value={value.preset}
        onChange={(e: any) => {
          const p = e.target.value as RangePreset;
          if (p === 'CUSTOM') {
            onChange({ preset: 'CUSTOM', from: appliedFrom ?? dayjs().subtract(7, 'day').toISOString(), to: appliedTo ?? dayjs().toISOString() });
          } else {
            onChange({ preset: p });
          }
        }}
        optionType="button"
        buttonStyle="solid"
      >
        {presets.map((p) => (
          <Radio.Button key={p} value={p}>{t(presetLabelKey(p))}</Radio.Button>
        ))}
      </Radio.Group>
      {value.preset === 'CUSTOM' && (
        <RangePicker
          allowClear={false}
          showTime
          value={rangeValue}
          onChange={(vals: any) => {
            if (!vals || !vals[0] || !vals[1]) return;
            onChange({ preset: 'CUSTOM', from: vals[0].toISOString(), to: vals[1].toISOString() });
          }}
        />
      )}
      {appliedFrom && appliedTo && (
        <Tag color="blue">
          {t('dashboard.range.applied', {
            from: dayjs(appliedFrom).format('YYYY-MM-DD HH:mm'),
            to: dayjs(appliedTo).format('YYYY-MM-DD HH:mm'),
          })}
        </Tag>
      )}
    </Space>
  );
}
